import { Component } from "react"
import PropTypes from "prop-types"
import { map } from "lodash"
import { CardContent, Paper, Tooltip as MuiTooltip, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material"
import { OfflineBolt } from "@mui/icons-material"
import { DATE } from "helpers/shapes"
import { formatLongDate } from "helpers/formatters/dates"
import { computeCSVData } from "helpers/csv"
import { smallPercentageFormatter } from "helpers/formatters/numbers"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import DetailRow from "./components/detail-row"
import "./style.scss"

export default class SecurityProfileForecast extends Component {
  static propTypes = {
    earliestDate: DATE,
    latestDate: DATE,
    startDate: DATE,
    endDate: DATE,
    selectedDate: DATE,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      disabled: PropTypes.bool.isRequired,
    })).isRequired,
    selectedCategory: PropTypes.string.isRequired,
    setDate: PropTypes.func.isRequired,
    setSelectedCategory: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    firstDate: PropTypes.string.isRequired,
    resetDate: PropTypes.func.isRequired,
    data: PropTypes.array.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })).isRequired,
    details: PropTypes.array.isRequired,
    toggleOnly: PropTypes.func.isRequired,
    only: PropTypes.string,
    forecastId: PropTypes.string.isRequired,
    trackCSVDownload: PropTypes.func.isRequired,
    fileName: PropTypes.string.isRequired,
    base: PropTypes.object,
    isPeriodIntraday: PropTypes.bool,
  }

  static ACTIVE_TOOLTIP_MSG = `
    Although a comparison is currently provided,
    this security's expected return is displayed in absolute terms.`

  componentDidUpdate() {
    const { firstDate, startDate: propsStartDate, selectedDate } = this.props
    const startDate = firstDate || propsStartDate

    if (selectedDate && (selectedDate < startDate)) {
      this.props.resetDate()
    }
  }

  onTabChanged = (_, id) => {
    const { setSelectedCategory } = this.props
    setSelectedCategory(id)
  }

  toggleOnly = (id, buttonName) => {
    const { toggleOnly, forecastId } = this.props
    toggleOnly(id, buttonName, forecastId)
  }

  render() {
    const {
      setDate,
      selectedDate,
      startDate,
      endDate,
      items,
      selectedCategory,
      options,
      toggle,
      focusChartLine,
      blurChartLine,
      focusedChartLine,
      data,
      details,
      only,
      fileName,
      trackCSVDownload,
      base,
      isPeriodIntraday,
    } = this.props

    return <CardContent className="card-content">
      <Paper className="content">
        {!data.length
          && <div className="components_charts_multi-line no-data forecast">No forecast data was provided for this security.</div>}

        {data.length > 0 && <>
          <div className="graph">
            <GraphTopBar {...{
              value: selectedCategory,
              tabs: map(options, ({ id, label }) => ({
                key: id,
                value: id,
                label,
                tooltip: base ? <MuiTooltip title={SecurityProfileForecast.ACTIVE_TOOLTIP_MSG}>
                  <OfflineBolt className="warning" />
                </MuiTooltip> : null,
              })),
              onTabChanged: this.onTabChanged,
              actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: true })} fileName={fileName} track={trackCSVDownload} />],
            }}
            />
            <MultiLine
              tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={smallPercentageFormatter} isOneDayMode={isPeriodIntraday} />}
              {...{
                tickFormat: smallPercentageFormatter,
                startDate,
                date: selectedDate,
                setDate,
                endDate,
                data,
                onlyItem: only,
                items,
                toggle,
                focusChartLine,
                blurChartLine,
                focusedChartLine,
                toggleOnly: this.toggleOnly,
              }}
            />
          </div>
          <div className="details">
            <h4 className="header">As of {formatLongDate(selectedDate)}</h4>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="75%" />
                  <TableCell align="right">Forecast</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {map(details, (value, index) => {
                  if (selectedCategory === "total" && index === 0) {
                    return null
                  } else {
                    return <DetailRow key={value.id} value={value} index={index} />
                  }
                })}
              </TableBody>
            </Table>
          </div>
        </>}
      </Paper>
    </CardContent>
  }
}
